import type { ErroApi } from '@crm/shared'

export class ErroRequisicao extends Error {
  constructor(
    readonly status: number,
    readonly corpo: ErroApi,
  ) {
    super(corpo.mensagem)
    this.name = 'ErroRequisicao'
  }

  /** Mensagem específica de um campo, para mostrar embaixo do input. */
  campo(nome: string): string | undefined {
    return this.corpo.campos?.[nome]
  }
}

export type Metodo = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

interface Opcoes {
  metodo?: Metodo
  corpo?: unknown
  query?: Record<string, string | number | undefined | null>
}

export async function api<T>(caminho: string, opcoes: Opcoes = {}): Promise<T> {
  const { metodo = 'GET', corpo, query } = opcoes

  let url = caminho
  if (query) {
    const parametros = new URLSearchParams()
    for (const [chave, valor] of Object.entries(query)) {
      if (valor !== undefined && valor !== null && valor !== '') {
        parametros.set(chave, String(valor))
      }
    }
    const texto = parametros.toString()
    if (texto) url += `?${texto}`
  }

  let resposta: Response
  try {
    resposta = await fetch(url, {
      method: metodo,
      credentials: 'same-origin',
      headers: corpo === undefined ? {} : { 'content-type': 'application/json' },
      body: corpo === undefined ? undefined : JSON.stringify(corpo),
    })
  } catch {
    // Sem rede ou servidor fora: o "Failed to fetch" do navegador não diz nada a quem lê.
    throw new Error(SEM_SERVIDOR)
  }

  return lerResposta<T>(resposta)
}

/**
 * Envia um arquivo (multipart) e lê a resposta como o `api()`. Eram quatro cópias
 * do mesmo envio (conversa, planilha, Materiais, Marketing), e três
 * delas mostravam "Unexpected token <" quando a resposta não era JSON.
 */
export async function enviarArquivo<T>(caminho: string, arquivo: File): Promise<T> {
  const dados = new FormData()
  dados.append('arquivo', arquivo)
  let resposta: Response
  try {
    resposta = await fetch(caminho, { method: 'POST', credentials: 'same-origin', body: dados })
  } catch {
    throw new Error(SEM_SERVIDOR)
  }
  return lerResposta<T>(resposta)
}

async function lerResposta<T>(resposta: Response): Promise<T> {
  if (resposta.status === 204) return undefined as T

  const texto = await resposta.text()
  /*
    Quem responde nem sempre é a api: com ela reiniciando, o proxy do Vite devolve
    500 vazio, e o do link público, uma página HTML. O JSON.parse disso aparecia na
    tela como "Unexpected token <" ou "Falha 500" (ADR 0075).
  */
  let dados: unknown = null
  let ehJson = true
  try {
    dados = texto ? (JSON.parse(texto) as unknown) : null
  } catch {
    ehJson = false
  }

  if (!resposta.ok) {
    const erro: ErroApi =
      dados && typeof dados === 'object' && 'mensagem' in dados
        ? (dados as ErroApi)
        : { erro: 'desconhecido', mensagem: mensagemSemCorpo(resposta.status) }
    throw new ErroRequisicao(resposta.status, erro)
  }

  // Resposta boa que não é JSON é a página da tela no lugar da api: não é dado.
  if (!ehJson) throw new Error('O servidor respondeu fora do esperado. Recarregue a página e tente de novo.')

  return dados as T
}

const SEM_SERVIDOR = 'Não consegui falar com o servidor. Confira a conexão e tente de novo.'

/** A recusa que não veio da api, com o código em palavras. */
function mensagemSemCorpo(status: number): string {
  if (status === 413) return 'O envio é grande demais.'
  if (status === 429) return 'Muitos pedidos seguidos. Espere um pouco e tente de novo.'
  if (status >= 500) {
    return `O servidor não respondeu: está reiniciando ou fora do ar (erro ${status}). Tente de novo em alguns segundos.`
  }
  return `O servidor recusou o pedido (erro ${status}).`
}
